import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { LANG_LABEL } from "../lib/siddhiText";
import { Download, ArrowRight, Flame, Sparkles, BookMarked, Quote } from "lucide-react";
import ParchmentBackground from "../components/ParchmentBackground";
import { useAuth } from "../context/AuthContext";
import "../styles/MyCraftzPage.css";
import "../styles/GraphPrintz.css";

const API = `${process.env.REACT_APP_BACKEND_URL}/api/mycraftz-install`;
const fmt = (iso) => (iso ? new Date(String(iso).length === 10 ? `${iso}T00:00:00` : iso).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" }) : "");
const CHOICE = { resonates: "Resonates", saveForLater: "Saved for later", notForMe: "Not for me" };

function Section({ label, children, testId }) {
  return (
    <div className="mj-section" data-testid={testId}>
      <p className="gpd-label">{label}</p>
      {children}
    </div>
  );
}

export function BookJourney({ b, readOnly: ro = false }) {
  const sid = b.siddhi;
  return (
    <article className="gpd-card mj-book" data-testid={`mj-book-${b.book_id}`}>
      <header className="mj-book-head">
        <h2 className="mj-book-title">{b.book_title}</h2>
        {b.stage && <span className="mj-stage" data-testid={`mj-stage-${b.book_id}`}>{b.stage}</span>}
      </header>

      {b.bodha.length > 0 && (
        <Section label="Bodha — your picks" testId={`mj-bodha-${b.book_id}`}>
          <p className="gpd-meaning">{b.bodha.map((c) => `${c.title}: ${CHOICE[c.choice] || c.choice}`).join(" · ")}</p>
        </Section>
      )}

      {b.seal && (
        <Section label="Sankalpa — sealed" testId={`mj-seal-${b.book_id}`}>
          <p className="gpd-meaning">{(b.seal.idea_titles || []).join(" · ")} · sealed {fmt(b.seal.sealed_at)}</p>
        </Section>
      )}

      {b.practices.length > 0 && (
        <Section label={`Sadhana — ${b.checkins.length} day${b.checkins.length === 1 ? "" : "s"} kept`} testId={`mj-sadhana-${b.book_id}`}>
          {b.practices.map((p) => (
            <div key={p.idea_id} className="mj-practice">
              <p className="mj-practice-title">{p.title}</p>
              <p className="gpd-meaning"><em>Phala:</em> {p.phala || "—"} · <em>Vighna:</em> {p.vighna || "—"} · <em>Upaya:</em> {p.upaya || "—"}</p>
              <p className="gpd-meaning"><Flame size={11} /> {p.checkins.length ? p.checkins.map(fmt).join(", ") : "No check-ins yet"}</p>
            </div>
          ))}
          {!ro && <Link to={`/sadhana?book=${b.book_id}`} className="mj-link" data-testid={`mj-edit-${b.book_id}`}>Edit your reflections <ArrowRight size={12} /></Link>}
        </Section>
      )}

      {sid && (
        <Section label={`Siddhi — ${b.siddhi_versions.length || 1} version${(b.siddhi_versions.length || 1) === 1 ? "" : "s"}`} testId={`mj-siddhi-${b.book_id}`}>
          <p className="mj-identity">“{sid.identity_statement}”</p>
          {b.siddhi_versions.map((v) => (
            <p key={v.version} className="gpd-meaning" data-testid={`mj-siddhi-v${v.version}-${b.book_id}`}>
              Version {v.version}{v.legacy ? " (legacy)" : ""} · {LANG_LABEL[v.lang] || LANG_LABEL.en} · {fmt(v.generated_at)}{v.note ? ` · note: “${v.note}”` : ""}
            </p>
          ))}
          {!ro && <Link to={`/siddhi?book=${b.book_id}`} className="mj-link">Open Siddhi <ArrowRight size={12} /></Link>}
        </Section>
      )}

      {(b.chapters.length > 0 || b.whispers.length > 0) && (
        <Section label="Chapters & Whispers" testId={`mj-story-${b.book_id}`}>
          {b.chapters.map((c) => <p key={c.id} className="gpd-meaning"><BookMarked size={11} /> {fmt(c.timestamp)} — {c.reflection}</p>)}
          {b.whispers.map((w) => <p key={w.id} className="gpd-meaning"><Quote size={11} /> {fmt(w.timestamp)} — {w.reflection}</p>)}
        </Section>
      )}
    </article>
  );
}

export default function MyJourneyPage() {
  const { isAuthenticated, loading, authHeaders } = useAuth();
  const navigate = useNavigate();
  const [books, setBooks] = useState(null);

  useEffect(() => {
    if (!loading && !isAuthenticated) navigate("/login", { replace: true, state: { from: "/my-journey" } });
  }, [loading, isAuthenticated, navigate]);

  useEffect(() => {
    if (!isAuthenticated) return;
    axios.get(`${API}/my-journey`, { headers: authHeaders() })
      .then((r) => setBooks(r.data.books || []))
      .catch(() => setBooks([]));
  }, [isAuthenticated, authHeaders]);

  const [pdfState, setPdfState] = useState("idle");
  const save = (blob, name) => {
    const url = URL.createObjectURL(blob);
    Object.assign(document.createElement("a"), { href: url, download: name }).click();
    URL.revokeObjectURL(url);
  };
  const downloadPdf = async () => {
    setPdfState("busy");
    try {
      const r = await axios.get(`${API}/my-journey.pdf`, { headers: authHeaders(), responseType: "blob" });
      save(r.data, "my-journey.pdf");
      setPdfState("idle");
    } catch (e) {
      setPdfState("error");
    }
  };
  const downloadJson = async () => {
    const r = await axios.get(`${API}/my-data`, { headers: authHeaders() });
    save(new Blob([JSON.stringify(r.data, null, 2)], { type: "application/json" }), "icraftz-my-data.json");
  };

  return (
    <div className="mc-page" data-testid="my-journey-page">
      <ParchmentBackground />
      <section className="gpd">
        <header className="gpd-head mj-head">
          <div>
            <Link to="/mycraftz" className="mj-link" data-testid="mj-back">← MyCraftz</Link>
            <h1 className="gpd-title">My Journey</h1>
            <p className="gpd-sub">Every book, from first understanding to who you are becoming.</p>
          </div>
          <div className="mj-downloads">
            <button type="button" className="gpd-cta mj-download" onClick={downloadPdf} disabled={pdfState === "busy"} data-testid="mj-download-pdf">
              <Download size={13} /> {pdfState === "busy" ? "Preparing your journey…" : "Download my journey (PDF)"}
            </button>
            <button type="button" className="mj-link mj-raw" onClick={downloadJson} data-testid="mj-download-json">Raw data (JSON)</button>
            {pdfState === "error" && <p className="sad-field-error" role="alert" data-testid="mj-download-error">Couldn&apos;t prepare the PDF. Please try again.</p>}
          </div>
        </header>
        <p className="gpd-caption mj-defs">
          <strong>Capture a Chapter</strong> — what moved you in a book. <strong>Siddhi</strong> — what happened when you practised it.{" "}
          <strong>Book Whispers</strong> — recommending a book outward.
        </p>
        {books === null ? (
          <p className="gpd-meaning">Opening your journey…</p>
        ) : books.length === 0 ? (
          <div className="gpd-card gpd-empty" data-testid="mj-empty">
            <p>Your journey begins with a book.</p>
            <Link to="/library" className="gpd-cta">Choose a book <Sparkles size={13} /></Link>
          </div>
        ) : (
          books.map((b) => <BookJourney key={b.book_id} b={b} />)
        )}
      </section>
    </div>
  );
}
